import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const SIZE = 60;
const MARGIN = 18;

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export default function ChatWidget() {
  const [side, setSide] = useState("right");
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [open, setOpen] = useState(false);

  const [messages, setMessages] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const sessionId = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `s-${Date.now()}`
  );
  const moved = useRef(false);
  const start = useRef({ x: 0, y: 0, px: 0, py: 0 });
  const scrollRef = useRef(null);

  // Initial position: bottom-right
  useEffect(() => {
    const init = () => {
      const w = window.innerWidth, h = window.innerHeight;
      setPos({ x: w - SIZE - MARGIN, y: h - SIZE - MARGIN - 8 });
      setSide("right");
    };
    init();
    const onResize = () => {
      setPos((p) => ({
        x: clamp(p.x, MARGIN, window.innerWidth - SIZE - MARGIN),
        y: clamp(p.y, MARGIN, window.innerHeight - SIZE - MARGIN),
      }));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
    moved.current = false;
    start.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y };
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - start.current.x;
    const dy = e.clientY - start.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 8) moved.current = true;
    setPos({
      x: clamp(start.current.px + dx, MARGIN, window.innerWidth - SIZE - MARGIN),
      y: clamp(start.current.py + dy, MARGIN, window.innerHeight - SIZE - MARGIN),
    });
  };

  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    if (!moved.current) {
      setOpen((o) => !o);
      return;
    }
    // Magnetise to nearest side
    const w = window.innerWidth;
    setPos((p) => {
      const goLeft = p.x + SIZE / 2 < w / 2;
      setSide(goLeft ? "left" : "right");
      return { x: goLeft ? MARGIN : w - SIZE - MARGIN, y: p.y };
    });
  };

  const ensureFaqs = useCallback(async () => {
    if (faqs.length) return;
    try {
      const { data } = await api.get("/chat/faqs");
      setFaqs(data.faqs || []);
    } catch {
      /* non-critical */
    }
  }, [faqs.length]);

  useEffect(() => {
    if (open) {
      ensureFaqs();
      if (messages.length === 0) {
        setMessages([{ role: "assistant", text: "Hi there! 👋 I'm the Winners Chapel Colchester assistant. Ask me about service times, our location, online services, prayer hours, or anything about the church." }]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: q });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setMessages((m) => [...m, { role: "assistant", text: typeof detail === "string" ? detail : "Sorry, I couldn't answer that right now. Please try the Contact page or call us." }]);
    } finally {
      setSending(false);
    }
  };

  const panelSideClass = side === "left" ? "left-4 sm:left-5" : "right-4 sm:right-5";

  return (
    <>
      {/* Draggable floating button */}
      <button
        data-testid="chat-fab"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          left: pos.x,
          top: pos.y,
          width: SIZE,
          height: SIZE,
          transition: dragging
            ? "none"
            : "left 0.6s cubic-bezier(0.22,1,0.36,1), top 0.6s cubic-bezier(0.22,1,0.36,1)",
          touchAction: "none",
        }}
        className="fixed z-[60] grid place-items-center rounded-full bg-chapel-red text-white shadow-[0_8px_22px_rgba(0,0,0,0.4)] hover:scale-105 active:scale-95 cursor-grab active:cursor-grabbing select-none"
        aria-label="Open chat assistant"
      >
        {open ? <X className="w-6 h-6 relative" /> : <MessageCircle className="w-7 h-7 relative" />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className={`fixed bottom-24 ${panelSideClass} z-[59] w-[calc(100vw-2rem)] sm:w-[380px] h-[70vh] sm:h-[520px] max-h-[560px] flex flex-col rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#160509]`}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-chapel-red to-[#8c0400] text-white">
            <span className="w-9 h-9 grid place-items-center rounded-full bg-white/20">
              <Sparkles className="w-5 h-5" />
            </span>
            <div className="leading-tight">
              <p className="font-heading font-bold text-sm">Chapel Assistant</p>
              <p className="text-[11px] text-white/80">Ask me anything about the church</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" className="ml-auto w-8 h-8 grid place-items-center rounded-full hover:bg-white/15 transition" aria-label="Close chat">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#160509]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-chapel-red text-white rounded-br-sm"
                      : "bg-white/8 text-zinc-100 rounded-bl-sm border border-white/10"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white/8 border border-white/10 rounded-2xl rounded-bl-sm px-3.5 py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-chapel-red" />
                </div>
              </div>
            )}

            {/* FAQ chips (only before the user has asked anything) */}
            {messages.length <= 1 && faqs.length > 0 && (
              <div className="pt-1 flex flex-wrap gap-2">
                {faqs.map((f, i) => (
                  <button
                    key={i}
                    data-testid={`chat-faq-${i}`}
                    onClick={() => send(f)}
                    className="text-xs text-zinc-200 bg-white/5 hover:bg-chapel-red hover:text-white border border-white/10 rounded-full px-3 py-1.5 transition"
                  >
                    {f}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 p-3 border-t border-white/10 bg-[#1a0609]"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:border-chapel-red focus:outline-none transition"
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={sending || !input.trim()}
              className="w-10 h-10 grid place-items-center rounded-full bg-chapel-red hover:bg-chapel-glow text-white disabled:opacity-50 transition shrink-0"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
